import React from 'react';
import { motion } from 'framer-motion';

// Fade in from bottom
export const FadeInUp = ({ children, delay = 0, duration = 0.6, className = '', style = {} }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    className={className}
    style={style}
  >
    {children}
  </motion.div>
);

// Fade in from left
export const FadeInLeft = ({ children, delay = 0, duration = 0.6, className = '', style = {} }) => (
  <motion.div
    initial={{ opacity: 0, x: -40 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    className={className}
    style={style}
  >
    {children}
  </motion.div>
);

// Fade in from right
export const FadeInRight = ({ children, delay = 0, duration = 0.6, className = '', style = {} }) => (
  <motion.div
    initial={{ opacity: 0, x: 40 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    className={className}
    style={style}
  >
    {children}
  </motion.div>
);

// Scale in
export const ScaleIn = ({ children, delay = 0, duration = 0.5, className = '', style = {} }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.92 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    className={className}
    style={style}
  >
    {children}
  </motion.div>
);

// Viewport-triggered fade in (for scroll animations)
export const ScrollFadeIn = ({ children, delay = 0, duration = 0.6, className = '', style = {}, direction = 'up' }) => {
  const directionMap = {
    up: { y: 30, x: 0 },
    down: { y: -30, x: 0 },
    left: { y: 0, x: -30 },
    right: { y: 0, x: 30 },
  };
  const offset = directionMap[direction] || directionMap.up;

  return (
    <motion.div
      initial={{ opacity: 0, ...offset }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
};

// Staggered container for child animations
export const StaggerContainer = ({ children, staggerDelay = 0.08, className = '', style = {} }) => (
  <motion.div
    initial="hidden"
    animate="visible"
    variants={{
      hidden: {},
      visible: {
        transition: {
          staggerChildren: staggerDelay,
        },
      },
    }}
    className={className}
    style={style}
  >
    {children}
  </motion.div>
);

// Staggered container triggered by viewport
export const ScrollStaggerContainer = ({ children, staggerDelay = 0.08, className = '', style = {} }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: '-30px' }}
    variants={{
      hidden: {},
      visible: {
        transition: {
          staggerChildren: staggerDelay,
        },
      },
    }}
    className={className}
    style={style}
  >
    {children}
  </motion.div>
);

// Stagger child item
export const StaggerItem = ({ children, className = '', style = {} }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 24 },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
      },
    }}
    className={className}
    style={style}
  >
    {children}
  </motion.div>
);

// Hover lift card wrapper
export const HoverLiftCard = ({ children, className = '', style = {}, onClick }) => (
  <motion.div
    whileHover={{ y: -6, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
    whileTap={{ scale: 0.98 }}
    transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
    className={className}
    style={style}
    onClick={onClick}
  >
    {children}
  </motion.div>
);

// Page wrapper with entrance animation
export const PageTransition = ({ children, className = '', style = {} }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -16 }}
    transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
    className={className}
    style={style}
  >
    {children}
  </motion.div>
);

// Fluid floating element
export const FloatingElement = ({ children, className = '', style = {}, amplitude = 8, duration = 4 }) => (
  <motion.div
    animate={{ y: [-amplitude, amplitude, -amplitude] }}
    transition={{ duration, repeat: Infinity, ease: 'easeInOut' }}
    className={className}
    style={style}
  >
    {children}
  </motion.div>
);
